import { useEffect, useRef, useState } from 'react';
import { endpoints } from './api';
import type { SyncOutcome } from './api';
import { applyDelta, clearLocalAdmissions, expireBundles, forgetSynced, pendingScans, storedBundles } from './offline';
import { recordClockOffset } from './session';

/**
 * The background loop (docs/scanner.md §5.1, §5.4): every 20 s push queued
 * offline scans in one idempotent batch per attempt; every 45 s pull each
 * stored bundle's delta and drop bundles that expired or were revoked. Both
 * are quiet when the network is down and resume when it returns.
 */

export interface SyncState {
  online: boolean;
  pending: number;
  last_sync_at: string | null;
  last_delta_at: string | null;
  conflicts: { scan_id: string; seat_label: string | null; at: string }[];
  wiped: string[];
  error: string | null;
}

const INITIAL: SyncState = { online: typeof navigator === 'undefined' ? true : navigator.onLine, pending: 0, last_sync_at: null, last_delta_at: null, conflicts: [], wiped: [], error: null };

export function useSyncLoop(enabled: boolean): SyncState & { syncNow: () => Promise<void> } {
  const [state, setState] = useState<SyncState>(INITIAL);
  const busy = useRef(false);

  const syncNow = async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      const queued = await pendingScans();
      setState((s) => ({ ...s, pending: queued.length }));
      if (queued.length > 0) {
        const batchId = `${queued[0]!.scan_id}:${queued.length}`;
        let outcome: SyncOutcome;
        try {
          outcome = await endpoints.sync({ client_batch_id: batchId, scans: queued.map(({ queued_at: _q, seat_label: _s, ...scan }) => scan) });
        } catch (cause) {
          setState((s) => ({ ...s, online: false, error: (cause as Error).message }));
          return;
        }
        await forgetSynced(outcome.results.map((r) => r.scan_id));
        for (const performanceId of new Set(queued.map((q) => q.performance_id))) await clearLocalAdmissions(performanceId);
        const conflicts = outcome.results.filter((r) => r.result === 'OFFLINE_CONFLICT').map((r) => ({ scan_id: r.scan_id, seat_label: queued.find((q) => q.scan_id === r.scan_id)?.seat_label ?? null, at: queued.find((q) => q.scan_id === r.scan_id)?.device_timestamp ?? '' }));
        setState((s) => ({ ...s, online: true, pending: 0, last_sync_at: new Date().toISOString(), conflicts: [...conflicts, ...s.conflicts].slice(0, 20), error: null }));
      } else setState((s) => ({ ...s, pending: 0 }));
    } finally {
      busy.current = false;
    }
  };

  const pullDeltas = async () => {
    const wiped = await expireBundles(new Date().toISOString());
    const bundles = await storedBundles();
    let online = true;
    for (const bundle of bundles) {
      try {
        const { delta } = await endpoints.delta(bundle.bundle_id, bundle.synced_at);
        const applied = await applyDelta(bundle.performance_id, delta);
        if (applied.wiped) wiped.push(bundle.performance_id);
      } catch (cause) {
        if ((cause as { isOffline?: boolean }).isOffline) online = false;
      }
    }
    try {
      const { server_time } = await endpoints.time();
      recordClockOffset(server_time);
      online = true;
    } catch (cause) {
      if ((cause as { isOffline?: boolean }).isOffline) online = false;
    }
    setState((s) => ({ ...s, online, last_delta_at: online ? new Date().toISOString() : s.last_delta_at, wiped: [...new Set([...s.wiped, ...wiped])] }));
  };

  useEffect(() => {
    if (!enabled) return;
    const onOnline = () => {
      setState((s) => ({ ...s, online: true }));
      void syncNow();
    };
    const onOffline = () => setState((s) => ({ ...s, online: false }));
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    void syncNow();
    void pullDeltas();
    const push = setInterval(() => void syncNow(), 20_000);
    const pull = setInterval(() => void pullDeltas(), 45_000);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      clearInterval(push);
      clearInterval(pull);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { ...state, syncNow };
}
